/**
 * @engine/core - Base Viewport Renderer
 *
 * Abstract base class for viewport rendering, shared between editor and player.
 *
 * @module @engine/core
 */

import type {
  IRenderContext,
  Camera2D,
  ShapeBatch,
  SpriteBatch,
  TextureHandle,
  TextureManager,
  PostProcessStack,
} from '@engine/render2d';
import type { Level } from '../Level';
import type {
  ViewportRenderRequest,
  ViewportRenderResult,
  ViewportCameraState,
} from './ViewportTypes';

/**
 * Base viewport renderer interface
 */
export interface IViewportRenderer {
  initialize(canvas: HTMLCanvasElement): Promise<void>;
  render(request: ViewportRenderRequest): ViewportRenderResult;
  getContext(): IRenderContext | null;
  getCamera(): Camera2D | null;
  resize(width: number, height: number): void;
  dispose(): void;
  isInitialized(): boolean;
}

/**
 * Render statistics
 */
export interface RenderStats {
  fps: number;
  frameTimeMs: number;
  drawCalls: number;
  triangles: number;
  vertices: number;
  textureBinds: number;
  spriteCount: number;
  vramUsed: number;
  vramAllocated: number;
  textureCount: number;
  bufferCount: number;
  pipelineChanges: number;
  bufferUploads: number;
  computeDispatches: number;
}

/**
 * Base implementation for viewport renderers
 */
export abstract class BaseViewportRenderer implements IViewportRenderer {
  protected ctx: IRenderContext | null = null;
  protected canvas: HTMLCanvasElement | null = null;
  protected camera: Camera2D | null = null;
  protected shapeBatch: ShapeBatch | null = null;
  protected spriteBatch: SpriteBatch | null = null;
  protected textureManager: TextureManager | null = null;
  protected initialized = false;

  protected stats: RenderStats = {
    fps: 0,
    frameTimeMs: 0,
    drawCalls: 0,
    triangles: 0,
    vertices: 0,
    textureBinds: 0,
    spriteCount: 0,
    vramUsed: 0,
    vramAllocated: 0,
    textureCount: 0,
    bufferCount: 0,
    pipelineChanges: 0,
    bufferUploads: 0,
    computeDispatches: 0,
  };

  protected lastFrameTime = 0;
  protected frameTimeAccum = 0;
  protected frameCount = 0;

  /** The canvas this renderer draws into (null before initialize). Used by hosts for
   *  screen↔world conversion backings (CameraBacking.screenToWorld). */
  get viewCanvas(): HTMLCanvasElement | null {
    return this.canvas;
  }

  async initialize(canvas: HTMLCanvasElement): Promise<void> {
    if (this.initialized) return;
    this.canvas = canvas;
    await this.onInitialize(canvas);
    this.initialized = true;
  }

  protected abstract onInitialize(canvas: HTMLCanvasElement): Promise<void>;

  render(request: ViewportRenderRequest): ViewportRenderResult {
    const startTime = performance.now();

    if (!this.ctx || !this.canvas) {
      return { drawCalls: 0, spriteCount: 0, renderTimeMs: 0 };
    }

    this.updateCamera(request.camera, request.width, request.height);
    this.ctx.beginFrame();
    const spriteCount = this.onRender(request);
    this.ctx.endFrame();

    const renderTimeMs = performance.now() - startTime;
    this.updateStats(renderTimeMs);

    const ctxStats = this.ctx.getStats();

    return {
      drawCalls: ctxStats.drawCalls,
      spriteCount,
      renderTimeMs,
    };
  }

  protected abstract onRender(request: ViewportRenderRequest): number;

  protected updateCamera(state: ViewportCameraState, width: number, height: number): void {
    if (!this.camera) return;
    this.camera.setPosition(state.x, state.y);
    this.camera.setZoom(state.zoom);
    this.camera.setRotation(state.rotation);
    this.camera.setViewport(width, height);
  }

  protected updateStats(frameTimeMs: number): void {
    this.frameTimeAccum += frameTimeMs;
    this.frameCount++;

    if (this.frameCount >= 10) {
      this.stats.frameTimeMs = this.frameTimeAccum / this.frameCount;
      this.stats.fps = 1000 / this.stats.frameTimeMs;
      this.frameTimeAccum = 0;
      this.frameCount = 0;
    }

    if (this.ctx) {
      const ctxStats = this.ctx.getStats();
      this.stats.drawCalls = ctxStats.drawCalls;
      this.stats.triangles = ctxStats.triangles;
      this.stats.vertices = ctxStats.vertices;
      this.stats.textureBinds = ctxStats.textureBinds;
      this.stats.vramUsed = ctxStats.vramUsed;
      this.stats.vramAllocated = ctxStats.vramAllocated;
      this.stats.textureCount = ctxStats.textureCount;
      this.stats.bufferCount = ctxStats.bufferCount;
      this.stats.pipelineChanges = ctxStats.pipelineChanges;
      this.stats.bufferUploads = ctxStats.bufferUploads;
      this.stats.computeDispatches = ctxStats.computeDispatches;
    }
  }

  getContext(): IRenderContext | null {
    return this.ctx;
  }

  getCamera(): Camera2D | null {
    return this.camera;
  }

  getStats(): RenderStats {
    return { ...this.stats };
  }

  resize(width: number, height: number): void {
    if (!this.canvas || !this.ctx) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.camera?.setViewport(width, height);
    this.ctx.handleResize?.();
    this.ctx.setViewport(0, 0, width, height);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  dispose(): void {
    this.onDispose();
    this.shapeBatch = null;
    this.spriteBatch = null;
    this.textureManager = null;
    this.camera = null;
    this.ctx = null;
    this.canvas = null;
    this.initialized = false;
  }

  protected onDispose(): void {
    // Default: no-op
  }
}

/**
 * Coordinate conversion: screen → world
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  camera: ViewportCameraState,
  viewportWidth: number,
  viewportHeight: number,
): { x: number; y: number } {
  const worldX = (screenX - viewportWidth / 2) / camera.zoom + camera.x;
  const worldY = camera.y - (screenY - viewportHeight / 2) / camera.zoom;
  return { x: worldX, y: worldY };
}

/**
 * Coordinate conversion: world → screen
 */
export function worldToScreen(
  worldX: number,
  worldY: number,
  camera: ViewportCameraState,
  viewportWidth: number,
  viewportHeight: number,
): { x: number; y: number } {
  const screenX = (worldX - camera.x) * camera.zoom + viewportWidth / 2;
  const screenY = (camera.y - worldY) * camera.zoom + viewportHeight / 2;
  return { x: screenX, y: screenY };
}
